import { useRef, useEffect, useState, use } from 'react';
import { getWhiteBoardDataByCurrentPresentationAndCurrentTime, filterWhiteboardInfo, drawWhiteboardByWhiteboardInfo, drawWhiteboardCursorByCursorInfo } from '../../utils/whiteBoadUtil'

const Whiteboard = ({ currentTime, whiteboardInfo, presentationInfo, cursorInfo }) => {
  // 移除了对 DataShareContext 的依赖

  const backgroundCanvasRef = useRef(null);
  const foregroundCanvasRef = useRef(null);
  const foregroundCursorCanvasRef = useRef(null);
  const containerRef = useRef(null);

  const [baseWidth, setBaseWidth] = useState(800);
  const [baseHeight, setBaseHeight] = useState(600);
  const aspectRatioRef = useRef(baseWidth / baseHeight);

  const [scaleRate, setScaleRate] = useState(1);
  const [dimensions, setDimensions] = useState({ width: baseWidth, height: baseHeight });
  const [currentWhiteboardId, setCurrentWhiteboardId] = useState(null);

  const [prePresentationUrl, setPrePresentationUrl] = useState(null)
  const [currentPresentationUrl, setCurrentPresentationUrl] = useState(null);
  const [currentPresentationId, setCurrentPresentationId] = useState(null); // 新增：当前课件ID

  const [tempWhiteboardInfoList, setTempWhiteboardInfoList] = useState([]);
  const [currentWhiteBoardInfos, setCurrentWhiteBoardInfos] = useState([]);

  // 魔棒
  const [currentCursorInfo, setCurrentCursorInfo] = useState(null);

  const [preloadedImages, setPreloadedImages] = useState([]);

  const preloadPresentationImages = async () => { 

      if (!presentationInfo) {
          return;
      }

      // 遍历presentationInfo，将所有图片解析出来(去重)
      const imageUrls = new Set();

      presentationInfo.forEach(presentationItem => {
          if (presentationItem.type === 288) {
              
              const presentations = presentationItem.data?.presentations;
              if (presentations) {
                  presentations.forEach(presentationItem => {
                      imageUrls.add(presentationItem.slide);
                  });
              }
          }
      });

      preloadImages(imageUrls)
  };

  // 预加载功能实现
  function preloadImages(urls) {

    urls.forEach(url => {
        const img = new Image();
        img.onload = () => {
          // 图片尺寸
          const { width, naturalWidth, height, naturalHeight } = img;
          preloadedImages.push({ url, width, naturalWidth, height, naturalHeight, img });
        };
        img.onerror = () => {
            console.log(`Error loading image ${url}.`);
        };
        img.src = url;
    });
  }

  function getPreloadedImage(url) {

    const preloadedImage = preloadedImages.find(img => img.url === url)

    if (preloadedImage && !isImageRecycled(preloadedImage.img)) {
      return preloadedImage;
    }

    return null;
  }

  // 检查图片是否被回收的函数
  const isImageRecycled = (img) => {
    if (!img || !img.complete) return true;
    
    // 方法1: 检查naturalWidth和naturalHeight
    if (img.naturalWidth === 0 && img.naturalHeight === 0) {
      return true;
    }
  };

  useEffect(() => { 
    // 每1分钟，检测预加载的图片，哪些被回收的
    const interval = setInterval(() => {
      const imagesToRemove = [];
      preloadedImages.forEach(preloadedImage => {
        if (isImageRecycled(preloadedImage.img)) {
          imagesToRemove.push(preloadedImage);
        }
      });
      console.log('[' + Date.now() + ']Removed images:', imagesToRemove);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // 在 updateCanvasSize 函数中：
  const updateCanvasSize = () => {

    if (!containerRef.current?.parentElement) return;
    
    const parent = containerRef.current.parentElement;
    const { width: parentWidth, height: parentHeight } = parent.getBoundingClientRect();
    
    // 计算逻辑尺寸（CSS像素）
    const logicalWidth = Math.min(parentWidth, parentHeight * aspectRatioRef.current);
    const logicalHeight = logicalWidth / aspectRatioRef.current;

    // 获取设备像素比
    const dpr = window.devicePixelRatio || 1;
    
    // 实际像素尺寸 = 逻辑尺寸 * DPR
    const actualWidth = logicalWidth * dpr;
    const actualHeight = logicalHeight * dpr;

    setDimensions({
      logicalWidth,  // 用于CSS样式
      logicalHeight,
      actualWidth,   // 用于canvas属性
      actualHeight,
      dpr            // 用于绘制缩放
    });
    
    const scorate = Math.min(actualWidth / baseWidth, actualHeight / baseHeight);

    setScaleRate(scorate);
  }

  // 更新画布尺寸
  useEffect(() => {

    updateCanvasSize();

    preloadPresentationImages();

    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  useEffect(() => { 

    // 重新计算比例
    aspectRatioRef.current = baseWidth / baseHeight;

    updateCanvasSize();

    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [baseWidth, baseHeight]);

  // 根据当前时间找到对应的课件
  useEffect(() => {

    if (!presentationInfo) return;

    // 遍历最近的288消息，缓存当前的课件列表
    let currentPresentations = null;
    for (let i = presentationInfo.length - 1; i >= 0; i--) {
      const presentation = presentationInfo[i];
      // 仅解析 258,288类型的课件信令
      if (presentation.milliseconds <= currentTime && presentation.type === 288) {
        currentPresentations = presentation.data.presentations;
        break;
      }
    }

    // 遍历最近的288，258消息，获取当前最新的课件
    let currentPresentation = null;
    // 遍历课件列表，查找<=currentTime的最后一张课件
    for (let i = presentationInfo.length - 1; i >= 0; i--) {
      const presentation = presentationInfo[i];
      // 仅解析 258,288类型的课件信令
      if (presentation.milliseconds <= currentTime && (presentation.type === 258 || presentation.type === 288)) {
        currentPresentation = presentation;
        break;
      }
    }

    let currentPresentationUrl = null;
    let currentPresentationId = null; // 新增：当前课件ID
    let currentWhiteboardId = null;
    // 再从 currentPresentation.presentations 里面寻找 presentationId = currentPresentation.presentationShare 的课件Url
    if (currentPresentation) {

      // 288消息，根据presentationShare比较
      if(currentPresentation.type === 288) {
        for (let i = currentPresentations?.length - 1; i >= 0; i--) {
          const presentation = currentPresentations[i];
          if (presentation.presentationId === currentPresentation.data?.presentationShare) {
            currentPresentationUrl = presentation.slide;
            currentPresentationId = presentation.presentationId; // 新增：设置当前课件ID
            currentWhiteboardId = presentation.whiteboardId;
            break;
          }
        }
      } else if (currentPresentation.type === 258) {
        // 258消息，根据 presentationId 比较
        for (let i = currentPresentations?.length - 1; i >= 0; i--) {
          const presentation = currentPresentations[i];
          if (presentation.presentationId === currentPresentation.data?.presentationId) {
            currentPresentationUrl = presentation.slide;
            currentPresentationId = presentation.presentationId; // 新增：设置当前课件ID
            currentWhiteboardId = presentation.whiteboardId;
            break;
          }
        }
      }
    }

    setCurrentPresentationUrl(currentPresentationUrl);
    setCurrentPresentationId(currentPresentationId); // 新增：设置本地状态
    setCurrentWhiteboardId(currentWhiteboardId);

  }, [presentationInfo, currentTime]);

  // 加载并绘制图片到 Canvas
  useEffect(() => {
    const loadAndDrawImage = async () => {
      if (!currentPresentationUrl || !backgroundCanvasRef.current) return;

      const preloadedImage = getPreloadedImage(currentPresentationUrl);
      if (preloadedImage) { 
        const canvas = backgroundCanvasRef.current;
        if(!canvas) return;

        const img = preloadedImage['img'].cloneNode(false);

        // 和 800:600相比，谁的比例更接近
        const imgScale = Math.min(800 / preloadedImage.naturalWidth, 600 / preloadedImage.naturalHeight);

        if(!prePresentationUrl || prePresentationUrl !== currentPresentationUrl) {

          setPrePresentationUrl(currentPresentationUrl);

          if( preloadedImage.width * imgScale !== baseWidth) {
            setBaseWidth(preloadedImage.naturalWidth * imgScale)
          }
          if( preloadedImage.height * imgScale !== baseHeight) {
            setBaseHeight(preloadedImage.naturalHeight * imgScale)
          }
        }

        const ctx = canvas.getContext('2d');
        
        // 清除画布时使用实际尺寸
        ctx.clearRect(0, 0, canvas.width, canvas.height); 

        // 清除前景白板
        // clearForegroundCanvas();
        
        // 使用高清绘制
        ctx.drawImage(
          img, 
          0, 
          0, 
          dimensions.actualWidth, 
          dimensions.actualHeight
        );

        console.log('use preload image, url:' + currentPresentationUrl);
      } else { 
        const img = new Image();
        img.src = currentPresentationUrl;
  
        img.onload = () => {
          const canvas = backgroundCanvasRef.current;
          if(!canvas) return;
  
          // 图片尺寸
          const { width, naturalWidth, height, naturalHeight } = img;
  
          // 和 800:600相比，谁的比例更接近
          const imgScale = Math.min(800 / naturalWidth, 600 / naturalHeight);
  
          if(!prePresentationUrl || prePresentationUrl !== currentPresentationUrl) {
  
            setPrePresentationUrl(currentPresentationUrl);
  
            if( width * imgScale !== baseWidth) {
              setBaseWidth(naturalWidth * imgScale)
            }
            if( height * imgScale !== baseHeight) {
              setBaseHeight(naturalHeight * imgScale)
            }
          }
  
          const ctx = canvas.getContext('2d');
          
          // 清除画布时使用实际尺寸
          ctx.clearRect(0, 0, canvas.width, canvas.height); 
          
          // 清除前景白板
          // clearForegroundCanvas();
        
          // 使用高清绘制
          ctx.drawImage(
            img, 
            0, 
            0, 
            dimensions.actualWidth, 
            dimensions.actualHeight
          );
        };

        console.log('not use preload image, url:' + currentPresentationUrl);
      }
    };

    // 清除前景白板
    clearForegroundCanvas();

    loadAndDrawImage();

  }, [currentPresentationUrl, dimensions]);

  const clearForegroundCanvas = () => { 

    const canvas = foregroundCanvasRef.current;
    if(!canvas) return;

    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height); 
  };

  // 加载并绘制白板元素：根据当前白板ID和当前时间去做匹配；后面可以在初始化的时候，先对白板ID进行分组，增加后期的检索效率
  useEffect(() => {

    // 根据白板ID 和 当前时间查询白板数据
    const whiteBoardInfos = getWhiteBoardDataByCurrentPresentationAndCurrentTime(whiteboardInfo, currentWhiteboardId, currentTime);

    // 当数量变化时，才进行后面的绘制操作
    if (tempWhiteboardInfoList.length !== whiteBoardInfos.length) {
      setTempWhiteboardInfoList(whiteBoardInfos);

      // 模拟撤销、重做、清空、移动、移除等操作，过滤出最终的数据
      const filteredWhiteboardInfos = filterWhiteboardInfo(whiteBoardInfos);
      setCurrentWhiteBoardInfos(filteredWhiteboardInfos);
    }
  }, [whiteboardInfo, currentTime, currentWhiteboardId, tempWhiteboardInfoList.length]);

  // 当白板内容和缩放比例改变时，重新绘制·
  useEffect(() => { 
    // 根据最新的信息tempWhiteboardInfoList, 处理绘制
    drawWhiteboardByWhiteboardInfo(foregroundCanvasRef, currentWhiteBoardInfos, baseWidth, baseHeight, scaleRate);
  }, [currentWhiteBoardInfos, scaleRate]);

  const getCursorInfoByCurrentTime = (cursorInfo, currentTime) => { 

    if (!cursorInfo) return null;

    for (let i = cursorInfo.length - 1; i >= 0; i--) { 
      if (cursorInfo[i].milliseconds <= currentTime) {
        return cursorInfo[i];
      }
    }

    return null;
  }

  // 当白板内容和缩放比例改变时，重新绘制·
  useEffect(() => { 

    if (!cursorInfo) return;

    // 根据currentTime 从 cursorInfo 获取 item.milliseconds <= currentTime - 300毫秒的数据
    const searchCurrentCursorInfo = getCursorInfoByCurrentTime(cursorInfo, currentTime);
    if(!searchCurrentCursorInfo || !currentCursorInfo || searchCurrentCursorInfo.milliseconds != currentCursorInfo.milliseconds) {
      setCurrentCursorInfo(searchCurrentCursorInfo);
    }
  }, [cursorInfo, currentTime, scaleRate]);

    // 当白板内容和缩放比例改变时，重新绘制·
  useEffect(() => { 

    if (!cursorInfo) return;

    // 根据最新的信息tempWhiteboardInfoList, 处理绘制
    drawWhiteboardCursorByCursorInfo(foregroundCursorCanvasRef, currentCursorInfo?.data, baseWidth, baseHeight, scaleRate);
  }, [currentCursorInfo, scaleRate]);

  return (
    <div 
      ref={containerRef} 
      className="whiteboard-container"
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '75%', // 4:3 比例
        backgroundColor: '#f5f5f5'
      }}
    >
      <canvas
        ref={backgroundCanvasRef}
        width={dimensions.actualWidth}
        height={dimensions.actualHeight}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: dimensions.logicalWidth,
          height: dimensions.logicalHeight,
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      />
      <canvas
        ref={foregroundCanvasRef}
        width={dimensions.actualWidth}
        height={dimensions.actualHeight}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: dimensions.logicalWidth,
          height: dimensions.logicalHeight
        }}
      />
      
      <canvas
        ref={foregroundCursorCanvasRef}
        width={dimensions.actualWidth}
        height={dimensions.actualHeight}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: dimensions.logicalWidth,
          height: dimensions.logicalHeight
        }}
      />
    </div>
  );
};

export default Whiteboard;